import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import React from 'react';

/**
 * 星级组件
 */
const StarRating: React.FC<{ rating: number; idPrefix?: string }> = ({
  rating,
  idPrefix = 'star',
}) => (
  <div className="flex shrink-0 items-center space-x-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <svg
        key={i}
        xmlns="http://www.w3.org/2000/svg"
        width="11"
        height="12"
        viewBox="0 0 11 12"
        fill="none"
        className="size-3 shrink-0 xl:size-[14px]">
        <path
          d="M5.07584 0.195939C5.21761 -0.0662299 5.59379 -0.0662302 5.73556 0.195939L7.2651 3.02447C7.31616 3.11889 7.40524 3.18691 7.50978 3.21129L10.5213 3.91371C10.7928 3.97703 10.9027 4.30382 10.7248 4.51834L8.64348 7.02724C8.57991 7.10387 8.54942 7.20264 8.55874 7.30176L8.87068 10.6202C8.89791 10.9098 8.59932 11.1188 8.33653 10.9941L5.5665 9.67931C5.46474 9.63101 5.34666 9.63101 5.2449 9.67931L2.47487 10.9941C2.21208 11.1188 1.9135 10.9098 1.94072 10.6202L2.25266 7.30176C2.26198 7.20264 2.23149 7.10387 2.16792 7.02724L0.0866372 4.51834C-0.0913177 4.30382 0.0186393 3.97703 0.290075 3.91371L3.30162 3.21129C3.40616 3.18691 3.49524 3.11889 3.5463 3.02447L5.07584 0.195939Z"
          fill={i < rating ? `url(#${idPrefix}-grad-${i})` : '#E5E5E5'}
        />
        {i < rating && (
          <defs>
            <linearGradient
              id={`${idPrefix}-grad-${i}`}
              x1="8.9057"
              y1="11.7013"
              x2="1.02741"
              y2="3.12461"
              gradientUnits="userSpaceOnUse">
              <stop stopColor="#FFC123" />
              <stop offset="0.698298" stopColor="#FFDE2F" />
              <stop offset="1" stopColor="#FFC42B" />
            </linearGradient>
          </defs>
        )}
      </svg>
    ))}
  </div>
);

/**
 * 解析高亮文本，将 <hl>text</hl> 转换为红色高亮
 */
const parseHighlightText = (text: string): React.ReactNode => {
  const parts = text.split(/(<hl>.*?<\/hl>)/g);
  return parts.map((part, index) => {
    if (part.startsWith('<hl>') && part.endsWith('</hl>')) {
      const content = part.slice(4, -5);
      return (
        <span key={index} className="font-semibold text-brand-red-100">
          {content}
        </span>
      );
    }
    return part;
  });
};

/**
 * 引号图标 - 开始引号
 */
const QuoteIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className={className || 'size-4'}>
    <g opacity="0.25">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.28101 3.03651C3.77082 4.64858 2.37629 6.52492 2.09744 8.66548C1.66332 11.9979 4.64338 13.629 6.15335 12.1634C7.66332 10.6979 6.75818 8.83772 5.66481 8.32925C4.57145 7.82075 3.90282 7.99785 4.01945 7.31835C4.13608 6.63888 5.69162 4.75495 7.05822 3.87758C7.14888 3.80048 7.18338 3.65088 7.09608 3.53738C7.03865 3.46275 6.92601 3.31637 6.75818 3.09822C6.61141 2.90747 6.47082 2.91461 6.28101 3.03651Z"
        fill="#CCCCCC"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.8904 3.03651C10.3802 4.64858 8.98567 6.52492 8.70681 8.66548C8.27271 11.9979 11.2528 13.629 12.7627 12.1634C14.2727 10.6979 13.3676 8.83772 12.2742 8.32925C11.1808 7.82075 10.5122 7.99785 10.6288 7.31835C10.7455 6.63888 12.301 4.75495 13.6676 3.87758C13.7583 3.80048 13.7928 3.65088 13.7054 3.53738C13.648 3.46275 13.5354 3.31637 13.3676 3.09822C13.2208 2.90747 13.0802 2.91461 12.8904 3.03651Z"
        fill="#CCCCCC"
      />
    </g>
  </svg>
);

/**
 * 引号图标 - 结束引号
 */
const QuoteEndIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    className={className || 'size-4'}>
    <g opacity="0.25">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.5745 19.4423C18.3398 17.0242 20.4316 14.2097 20.8499 11.9988C21.501 7.00015 17.031 4.55354 14.766 6.75195C12.501 8.95036 13.8587 11.7405 15.4987 12.5032C17.1388 13.266 18.1417 13.0003 17.9668 14.0196C17.7918 15.0388 15.4585 17.8647 13.4087 19.1808C13.2727 19.2964 13.2209 19.5208 13.352 19.691C13.4381 19.803 13.607 20.0225 13.8587 20.3498C14.0789 20.6359 14.2898 20.6252 14.5745 19.4423Z"
        fill="#CCCCCC"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.6605 19.4423C8.42579 17.0242 10.5176 14.2097 10.9359 11.9988C11.5871 7.00015 7.11699 4.55354 4.85203 6.75195C2.58707 8.95036 3.9447 11.7405 5.58476 12.5032C7.22481 13.266 8.22776 13.0003 8.05281 14.0196C7.87787 15.0388 5.54456 17.8647 3.49464 19.1808C3.35864 19.2964 3.30684 19.5208 3.43789 19.691C3.524 19.803 3.69295 20.0225 3.9447 20.3498C4.16485 20.6359 4.37576 20.6252 4.6605 19.4423Z"
        fill="#CCCCCC"
      />
    </g>
  </svg>
);

/**
 * 奖项标签背景 - 两侧箭头缺口形状 (PC端 lg/xl)
 */
const AwardBadgeBackground: React.FC<{ className?: string }> = ({
  className,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 504 40"
    fill="none"
    className={className}
    preserveAspectRatio="none">
    <path
      d="M0.0725131 1.37139C-0.190233 0.714528 0.293525 0 1.00099 0H502.047C502.754 0 503.238 0.714526 502.975 1.37139L495.524 20L502.975 38.6286C503.238 39.2855 502.754 40 502.047 40H1.00097C0.293505 40 -0.190233 39.2855 0.0725126 38.6286L7.52396 20L0.0725131 1.37139Z"
      fill="#FBEEEE"
    />
    <path
      d="M1.00052 0.5H502.047C502.401 0.500308 502.642 0.857306 502.511 1.18555L495.06 19.8145L494.986 20L495.06 20.1855L502.511 38.8145C502.642 39.1427 502.401 39.4997 502.047 39.5H1.00052C0.647106 39.4997 0.405474 39.1427 0.536652 38.8145L7.98782 20.1855L8.06204 20L7.98782 19.8145L0.536652 1.18555C0.405482 0.857304 0.647102 0.500317 1.00052 0.5Z"
      stroke="#CD292A"
      strokeOpacity="0.04"
    />
  </svg>
);

/**
 * 奖项标签背景 - 两侧箭头缺口形状 (移动端)
 */
const MobileAwardBadgeBackground: React.FC<{ className?: string }> = ({
  className,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 344 36"
    fill="none"
    className={className}
    preserveAspectRatio="none">
    <path
      d="M0.0725131 1.23425C-0.190233 0.643075 0.293525 0 1.00099 0H342.047C342.754 0 343.238 0.643074 342.975 1.23425L336.524 18L342.975 34.7657C343.238 35.3569 342.754 36 342.047 36H1.00097C0.293505 36 -0.190233 35.3569 0.0725126 34.7657L6.52396 18L0.0725131 1.23425Z"
      fill="#FBEEEE"
    />
    <path
      d="M1.00052 0.5H342.047C342.401 0.500277 342.642 0.822576 342.511 1.11844L336.06 17.8833L335.986 18L336.06 18.1167L342.511 34.8816C342.642 35.1774 342.401 35.4997 342.047 35.5H1.00052C0.647106 35.4997 0.405474 35.1774 0.536652 34.8816L6.98782 18.1167L7.06204 18L6.98782 17.8833L0.536652 1.11844C0.405482 0.822574 0.647102 0.500286 1.00052 0.5Z"
      stroke="#CD292A"
      strokeOpacity="0.04"
    />
  </svg>
);

/**
 * 优秀徽章
 */
const ExcellentBadge: React.FC<{ className?: string }> = ({ className }) => (
  <div
    className={`absolute z-10 ${className || 'left-[256px] top-[-20px] h-20 w-[100px]'}`}>
    <Image
      src="/images/wukong-reviews/excellent.png"
      alt="Excellent"
      fill
      className="object-contain"
    />
  </div>
);

export interface CaseCardProps {
  caseId: number;
  image: string;
  rating: number;
}

/**
 * 案例卡片组件 - PC端
 * lg (1024-1440px): 卡片984px, 左侧600px, 右侧384px
 * xl (1441px+): 卡片1320px, 左侧800px, 右侧520px
 */
export const CaseCard: React.FC<CaseCardProps> = ({
  caseId,
  image,
  rating,
}) => {
  const { t } = useTranslation('wukong-reviews');

  const name = t(`案例-${caseId}-姓名`);
  const region = t(`案例-${caseId}-地区`);
  const age = t(`案例-${caseId}-年龄`);
  const subjects = t(`案例-${caseId}-学科`);
  const duration = t(`案例-${caseId}-时长`);
  const title = t(`案例-${caseId}-标题`);
  const description = t(`案例-${caseId}-介绍`);
  const identity = t(`案例-${caseId}-身份`);

  return (
    <div className="isolate flex items-stretch overflow-hidden rounded-xl border border-border-deep bg-bg-card_gray_light bg-clip-padding">
      {/* 左侧内容 */}
      <div className="z-[2] flex shrink-0 flex-col justify-between bg-bg-card_white px-4 py-8 lg:w-[600px] lg:px-4 lg:py-8 xl:w-[800px] xl:px-6 xl:py-11">
        {/* 标题和描述 */}
        <div className="flex w-full space-x-2">
          <QuoteIcon className="mt-1 size-4 shrink-0 lg:size-4" />
          <div className="flex min-w-0 flex-1 flex-col space-y-4">
            <h3 className="text-[18px] font-extrabold leading-[1.2] text-text-primary lg:text-[20px] xl:text-[24px]">
              {title}
            </h3>
            <div className="flex w-full items-end space-x-2">
              <p className="min-w-0 flex-1 whitespace-pre-wrap text-[14px] leading-[1.4] text-text-secondary_1 lg:text-[14px] lg:leading-[1.4] xl:text-[16px] xl:leading-[1.4]">
                {description}
              </p>
              <QuoteEndIcon className="mb-1 size-4 shrink-0 lg:size-4" />
            </div>
          </div>
        </div>

        {/* 奖项标签 */}
        <div className="relative ml-6 mr-6 mt-4 flex h-10 items-center justify-center px-[52px] lg:h-10 xl:h-11">
          <AwardBadgeBackground className="absolute bottom-0 left-0 right-0 top-0 h-full w-full" />
          <div className="relative flex items-center space-x-3 px-6">
            <span className="text-[28px]">🥇</span>
            <span className="whitespace-nowrap text-[14px] text-text-primary lg:text-[14px] xl:text-[16px]">
              {parseHighlightText(identity)}
            </span>
          </div>
        </div>
      </div>

      {/* 右侧图片和用户信息 */}
      <div className="z-[1] flex min-w-0 flex-1 flex-col justify-center space-y-6 overflow-hidden bg-bg-card_gray_light px-3 py-8 lg:space-y-6 lg:px-3 lg:py-8 xl:px-10 xl:py-11">
        <div className="flex w-full flex-col items-center">
          {/* 图片容器 */}
          <div className="relative h-[180px] w-[360px] lg:h-[180px] lg:w-[360px] xl:h-[220px] xl:w-[440px]">
            <div className="absolute left-1/2 top-0 h-[172px] w-[288px] -translate-x-1/2  lg:h-[172px] lg:w-[288px] xl:h-[220px] xl:w-[368px]">
              <Image
                src={image}
                alt={name}
                fill
                quality={100}
                className="object-contain"
              />
            </div>
            <ExcellentBadge className="left-[256px] top-[-20px] h-20 w-[100px] lg:left-[256px] lg:top-[-20px] lg:h-20 lg:w-[100px] xl:left-[332px] xl:top-[-28px] xl:h-[98px] xl:w-[124px]" />
          </div>
        </div>

        {/* 用户信息 */}
        <div className="flex w-full flex-col items-center space-y-3">
          <h4 className="text-[18px] font-extrabold leading-[1.2] text-text-primary lg:text-[20px] xl:text-[24px]">
            {name}
          </h4>
          <div className="flex flex-col items-center space-y-2">
            <div className="flex items-center space-x-1.5 text-center lg:space-x-1.5 xl:space-x-3">
              <span className="text-[12px] text-text-primary lg:text-[12px] xl:text-[14px]">
                {region}
              </span>
              <span className="h-3 w-px bg-border-deep" />
              <span className="text-[12px] text-text-primary lg:text-[12px] xl:text-[14px]">
                {age}
              </span>
              <span className="h-3 w-px bg-border-deep" />
              <span className="text-[12px] text-text-primary lg:text-[12px] xl:text-[14px]">
                {t('案例-时长标签')}
                {duration}
              </span>
            </div>
            <div className="flex items-center space-x-1.5 lg:space-x-1.5 xl:space-x-3">
              <span className="text-[12px] text-text-primary lg:text-[12px] xl:text-[14px]">
                {subjects}
              </span>
              <span className="h-3 w-px bg-border-deep" />
              <StarRating idPrefix={`case-pc-${caseId}`} rating={rating} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 案例卡片组件 - 移动端
 */
export const MobileCaseCard: React.FC<CaseCardProps> = ({
  caseId,
  image,
  rating,
}) => {
  const { t, i18n } = useTranslation('wukong-reviews');
  const isZh = i18n.language === 'zh';

  const name = t(`案例-${caseId}-姓名`);
  const region = t(`案例-${caseId}-地区`);
  const age = t(`案例-${caseId}-年龄`);
  const subjects = t(`案例-${caseId}-学科`);
  const duration = t(`案例-${caseId}-时长`);
  const title = t(`案例-${caseId}-标题`);
  const description = t(`案例-${caseId}-介绍`);
  const identity = t(`案例-${caseId}-身份`);

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-xl border border-border-deep bg-bg-card_gray_light bg-clip-padding">
      {/* 上方图片和用户信息 */}
      <div className="flex flex-col items-center space-y-4 px-4 pb-6 pt-8">
        {/* 图片容器 */}
        <div className="relative w-full px-6">
          <div className="relative w-full" style={{ paddingBottom: '59.82%' }}>
            <div className="absolute bottom-0 left-0 right-0 top-0">
              <Image
                src={image}
                alt={name}
                fill
                quality={90}
                className="object-cover"
              />
            </div>
          </div>
          <div className="absolute -right-3 -top-4 z-10 h-[80px] w-[100px]">
            <Image
              src="/images/wukong-reviews/excellent-mobile.png"
              alt="Excellent"
              fill
              className="object-contain"
              unoptimized
            />
          </div>
        </div>

        {/* 用户信息 */}
        <div className="flex w-full flex-col items-center space-y-2">
          <h4 className="text-[18px] font-extrabold leading-[1.2] text-text-primary">
            {name}
          </h4>
          <div className="flex w-full flex-col items-center space-y-1.5">
            <div className="flex flex-wrap items-center justify-center space-x-1 text-[12px]">
              <span className="text-text-primary">{region}</span>
              <span className="h-2.5 w-px bg-border-deep" />
              <span className="text-text-primary">{age}</span>
              <span className="h-2.5 w-px bg-border-deep" />
              <span className="text-text-primary">
                {t('案例-时长标签')}
                {duration}
              </span>
            </div>
            <div className="flex items-center justify-center space-x-1 text-[12px]">
              <span className="text-text-primary">{subjects}</span>
              <span className="h-2.5 w-px bg-border-deep" />
              <StarRating idPrefix={`case-${caseId}`} rating={rating} />
            </div>
          </div>
        </div>
      </div>

      {/* 下方内容 */}
      <div className="flex flex-1 flex-col justify-between space-y-3 border-t border-border-light bg-bg-card_white px-4 py-4">
        {/* 标题和描述 */}
        <div className="flex flex-col space-y-2">
          <h3 className="text-[20px] font-extrabold leading-[1.2] text-text-primary">
            {title}
          </h3>
          <p className="text-[16px] leading-[1.5] text-text-secondary_1">
            {description}
          </p>
        </div>

        {/* 奖项标签 */}
        <div className="relative mt-auto flex h-8 w-full items-center justify-center">
          <MobileAwardBadgeBackground className="absolute bottom-0 left-[-4px] right-0 top-0 h-full w-[calc(100%+8px)]" />
          <div className="relative flex items-center space-x-1 whitespace-nowrap px-3">
            <span className="text-[18px]">🥇</span>
            <span
              className={`text-[11px] text-text-primary ${isZh ? '-tracking-wide' : ''}`}>
              {parseHighlightText(identity)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
